<template>
  <v-container fluid class="py-8">
    <!-- Heading -->
    <div class="mb-6">
      <div v-if="user">
        <h2 class="text-h5 font-weight-bold">Welcome, {{ user.first_name }} {{ user.last_name }}</h2>
      </div>
      <div v-else>
        <h2 class="text-h5 font-weight-bold">Welcome, Guest</h2>
      </div>

      <span class="text-overline text-medium-emphasis d-block mt-4">Current Bookings</span>
    </div>

    <!-- Loading state -->
    <v-row v-if="isLoading" class="mb-4" dense>
      <v-col v-for="n in itemsPerPage" :key="n" cols="12" sm="6" md="4" lg="3">
        <v-skeleton-loader type="image, text, text" class="rounded-lg" />
      </v-col>
    </v-row>

    <!-- Empty state -->
    <v-alert
      v-else-if="(bookings || []).length === 0"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      You don’t have any bookings yet.
    </v-alert>

    <!-- Cards -->
    <v-row v-else class="mb-4" dense>
      <v-col
        v-for="card in pagedBookings"
        :key="card.id"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <BookedCruiseCard :booking="card" />
      </v-col>
    </v-row>

    <!-- Pagination -->
    <v-row class="mt-2" align="center" justify="space-between">
      <v-col cols="auto">
        <small class="text-medium-emphasis">
          Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ totalCount }}
        </small>
      </v-col>
      <v-col cols="auto" class="d-flex align-center ga-4">
        <v-pagination
          v-model="page"
          :length="totalPages"
          :total-visible="7"
          density="comfortable"
          prev-icon="mdi-chevron-left"
          next-icon="mdi-chevron-right"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, onActivated } from 'vue'
import { useStore } from 'vuex'
import { supabase } from '@/supabaseClient'
import BookedCruiseCard from '@/components/BookedCruiseCard.vue'

const store = useStore()
const user = computed(() => store.state.user)

const bookings = ref([])
const isLoading = ref(true)

// client-side pagination
const page = ref(1)
const itemsPerPage = ref(3)
const totalCount = ref(0)

// single source of truth for uid (don’t rely only on store)
const uid = ref(null)

const totalPages = computed(() =>
  Math.max(1, Math.ceil((totalCount.value || 0) / itemsPerPage.value))
)
const pageStart = computed(() => (page.value - 1) * itemsPerPage.value)
const pageEnd = computed(() => Math.min(pageStart.value + itemsPerPage.value, totalCount.value || 0))
const pagedBookings = computed(() => bookings.value.slice(pageStart.value, pageEnd.value))

async function fetchBookings() {
  if (!user.value?.uid) { isLoading.value = false; return }

  isLoading.value = true
  try {
    // grab ALL for client-side pagination (simple)
    const { data, error, count } = await supabase
      .from('bookings')
      .select('*', { count: 'exact' })
      .eq('user_id', user.value.uid)
      .order('created_at', { ascending: false })

    if (error) throw error

    bookings.value = data || []
    totalCount.value = count ?? bookings.value.length

    // keep page in range if size changed
    if (page.value > totalPages.value) page.value = totalPages.value
  } catch (e) {
    console.error('Error fetching bookings:', e)
    bookings.value = []
    totalCount.value = 0
  } finally {
    isLoading.value = false
  }
}

// 1) Watch uid and fetch (or stop spinner for guests)
watch(uid, (u) => {
  if (u) fetchBookings(u)
  else {
    isLoading.value = false
    bookings.value = []
    totalCount.value = 0
    page.value = 1
  }
}, { immediate: true })

// 2) Initialize uid on mount (covers hard refresh)
onMounted(async () => {
  const { data: { session } } = await supabase.auth.getSession()
  uid.value = session?.user?.id ?? user.value?.uid ?? null
})

// 3) React to auth changes (login/logout after mount)
const { data: { subscription } } = supabase.auth.onAuthStateChange((_evt, session) => {
  uid.value = session?.user?.id ?? null
})
onUnmounted(() => subscription?.unsubscribe?.())

// 4) If you keep this page alive, refresh when re-activated
onActivated(() => {
  if (uid.value) fetchBookings(uid.value)
})

// if you want to refetch when page size changes (optional with client-side paginate)
watch(itemsPerPage, () => {
  // just clamp the page if needed; no fetch needed since we have all rows
  if (page.value > totalPages.value) page.value = totalPages.value
})
</script>

<style scoped>
/* tiny polish */
</style>
